import { Controller } from "@hotwired/stimulus"

// Records a voice note in the browser for a report's Audio field (Baustein „Audio“, BAUTASK-622)
// and hands the finished file to the field's file-upload controller, which uploads it like a
// picked file: the element dispatches `audio-recorder:recorded` with `{ file }`, and the Audio
// field wires it to `file-upload#uploadRecorded`.
//
// AAC in MP4 first: the mobile app plays the report's recordings with AVPlayer on iOS, which
// cannot decode WebM. Chrome and Safari record MP4; Firefox only WebM, which stays the fallback.
export const PREFERRED_MIME_TYPES = [
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/webm;codecs=opus",
  "audio/webm",
]

export function recordingMimeType(isSupported = (type) => MediaRecorder.isTypeSupported(type)) {
  return PREFERRED_MIME_TYPES.find((type) => isSupported(type)) ?? null
}

export function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Math.floor(totalSeconds))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`
}

// One recording: its own stream, recorder, chunks and cap timer. MediaRecorder delivers
// `dataavailable` and `stop` on a later task, so none of this may live on the controller:
// a late `stop` must only ever touch the recording it belongs to. `onDone` runs once.
export class RecordingSession {
  constructor(stream, mimeType, { maxMs, onDone }) {
    this.stream = stream
    this.mimeType = mimeType.split(";")[0]
    this.chunks = []
    this.failed = false
    this.onDone = onDone
    try {
      this.recorder = new MediaRecorder(stream, { mimeType })
      this.recorder.addEventListener("dataavailable", (event) => {
        if (event.data.size > 0) this.chunks.push(event.data)
      })
      this.recorder.addEventListener("error", () => {
        this.failed = true
        this.stop()
      })
      this.recorder.addEventListener("stop", () => this.settle())
      this.recorder.start()
    } catch (error) {
      this.release()
      throw error
    }
    this.timer = setTimeout(() => this.stop(), maxMs)
  }

  stop() {
    if (this.recorder.state !== "inactive") this.recorder.stop()
  }

  // Ends the recording without handing it on (the field left the page).
  discard() {
    this.onDone = null
    this.stop()
    this.release()
  }

  release() {
    clearTimeout(this.timer)
    this.stream.getTracks().forEach((track) => track.stop())
  }

  settle() {
    this.release()
    const done = this.onDone
    this.onDone = null
    done?.({ chunks: this.chunks, mimeType: this.mimeType, failed: this.failed })
  }
}

export default class extends Controller {
  static targets = ["button", "label", "status"]

  static values = {
    // CustomReportField::AUDIO_RECORDING_MAX_DURATION, set by the view.
    maxSeconds: Number,
    filename: String,
    start: String,
    recording: String,
    micDenied: String,
    failed: String,
    stopFirst: String,
  }

  connect() {
    this.state = "idle" // idle → starting → recording → stopping → idle
    // Without MediaRecorder (old browser, insecure context) the field stays upload-only.
    const supported =
      typeof window.MediaRecorder !== "undefined" &&
      navigator.mediaDevices?.getUserMedia != null &&
      recordingMimeType() != null
    this.element.hidden = !supported

    // A report saved mid-recording would silently go without it.
    this.form = this.element.closest("form")
    this.guardSubmit = (event) => {
      if (this.state === "idle") return
      event.preventDefault()
      event.stopImmediatePropagation()
      this.showStatus(this.stopFirstValue)
    }
    this.form?.addEventListener("submit", this.guardSubmit, true)
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.guardSubmit, true)
    this.attempt = null
    this.session?.discard()
    this.session = null
    this.enter("idle")
  }

  async toggle() {
    if (this.state === "recording") {
      this.enter("stopping")
      this.session.stop()
      return
    }
    // A click while the permission prompt is open or the last recording is still settling.
    if (this.state !== "idle") return
    await this.begin()
  }

  async begin() {
    this.showStatus("")
    this.enter("starting")
    const attempt = (this.attempt = {})

    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      if (this.attempt !== attempt) return
      this.enter("idle")
      this.showStatus(this.micDeniedValue)
      return
    }
    // Disconnected while the permission prompt was open.
    if (this.attempt !== attempt) {
      stream.getTracks().forEach((track) => track.stop())
      return
    }

    let session
    try {
      session = new RecordingSession(stream, recordingMimeType(), {
        maxMs: this.maxSecondsValue * 1000,
        onDone: (result) => this.finish(session, result),
      })
    } catch {
      this.enter("idle")
      this.showStatus(this.failedValue)
      return
    }
    this.session = session
    this.startedAt = Date.now()
    this.enter("recording")
  }

  finish(session, { chunks, mimeType, failed }) {
    if (this.session !== session) return
    this.session = null
    this.enter("idle")
    if (failed) {
      this.showStatus(this.failedValue)
      return
    }
    if (chunks.length === 0) return

    const extension = mimeType === "audio/mp4" ? "m4a" : "webm"
    const stamp = new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    const file = new File(chunks, `${this.filenameValue} ${stamp.replace(":", "-")}.${extension}`, {
      type: mimeType,
    })
    this.dispatch("recorded", { detail: { file } })
  }

  enter(state) {
    this.state = state
    const recording = state === "recording" || state === "stopping"
    clearInterval(this.tick)
    if (state === "recording") this.tick = setInterval(() => this.updateTimer(), 500)
    if (!recording) this.startedAt = null

    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = state === "starting" || state === "stopping"
      this.buttonTarget.setAttribute("aria-pressed", recording ? "true" : "false")
      this.buttonTarget.classList.toggle("border-red-500", recording)
      this.buttonTarget.classList.toggle("text-red-600", recording)
    }
    if (this.hasLabelTarget && state !== "stopping") {
      this.labelTarget.textContent =
        state === "recording"
          ? this.recordingValue.replace("%{time}", formatDuration(0))
          : this.startValue
    }
  }

  updateTimer() {
    if (!this.hasLabelTarget || this.startedAt == null) return
    const elapsed = (Date.now() - this.startedAt) / 1000
    this.labelTarget.textContent = this.recordingValue.replace("%{time}", formatDuration(elapsed))
  }

  showStatus(message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    this.statusTarget.hidden = message === ""
  }
}
